import React, { useState } from 'react';
import {
  Monitor,
  Smartphone,
  Apple,
  Shield,
  Trash2,
  Upload,
  Lock,
  FileText,
  CheckCircle,
  XCircle,
  Laptop,
  Terminal,
  HardDrive,
  Wifi,
  Key,
  UserX,
  Download,
  Settings,
  AlertTriangle,
  Activity,
  Database,
  Server,
  Code,
  Package,
  GitBranch,
  Zap
} from 'lucide-react';

const iconLookup = {
  terminal: Terminal,
  file: FileText,
  shield: Shield
};

const BaselineDeviceCards = ({ baselineType, scripts = [], onSelectScript, onCreatePolicy, customPolicies = {} }) => {
  const [selectedDeviceType, setSelectedDeviceType] = useState(null);

  // Generate policies based on baseline type and device type
  const generatePolicies = (baselineType, deviceType) => {
    const policies = {
      'patch-management': {
        windows: [
          {
            id: 'win-patch-defer',
            title: 'Defer Windows Updates',
            description: 'Configure Windows Update deferral periods for quality and feature updates.',
            icon: Download,
            color: 'text-blue-500',
            risk: 'Medium',
            deviceType: 'windows',
            type: 'script',
            lang: 'PowerShell'
          },
          {
            id: 'win-patch-active-hours',
            title: 'Set Active Hours',
            description: 'Configure active hours to prevent automatic reboots during work hours.',
            icon: Settings,
            color: 'text-green-500',
            risk: 'Low',
            deviceType: 'windows',
            type: 'script',
            lang: 'PowerShell'
          },
          {
            id: 'win-patch-auto-install',
            title: 'Auto-Install Critical Updates',
            description: 'Automatically install critical security updates without user intervention.',
            icon: CheckCircle,
            color: 'text-orange-500',
            risk: 'High',
            deviceType: 'windows',
            type: 'script',
            lang: 'PowerShell'
          },
          {
            id: 'win-patch-schedule',
            title: 'Schedule Update Installation',
            description: 'Schedule Windows Update installation during off-hours.',
            icon: Activity,
            color: 'text-purple-500',
            risk: 'Medium',
            deviceType: 'windows',
            type: 'script',
            lang: 'PowerShell'
          }
        ],
        android: [
          {
            id: 'android-patch-check',
            title: 'Check for System Updates',
            description: 'Force check for Android system and security updates.',
            icon: Download,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'android',
            type: 'command'
          },
          {
            id: 'android-patch-auto-update',
            title: 'Enable Auto-Update',
            description: 'Enable automatic app and system updates on Android device.',
            icon: CheckCircle,
            color: 'text-green-500',
            risk: 'Medium',
            deviceType: 'android',
            type: 'command'
          },
          {
            id: 'android-patch-security',
            title: 'Install Security Patches',
            description: 'Install pending Android security patches immediately.',
            icon: Shield,
            color: 'text-orange-500',
            risk: 'High',
            deviceType: 'android',
            type: 'command'
          }
        ],
        macos: [
          {
            id: 'macos-patch-auto',
            title: 'Enable Auto-Update',
            description: 'Enable automatic macOS software updates.',
            icon: Download,
            color: 'text-blue-500',
            risk: 'Medium',
            deviceType: 'macos',
            type: 'script',
            lang: 'Shell'
          },
          {
            id: 'macos-patch-security',
            title: 'Install Security Updates',
            description: 'Install critical macOS security updates immediately.',
            icon: Shield,
            color: 'text-orange-500',
            risk: 'High',
            deviceType: 'macos',
            type: 'script',
            lang: 'Shell'
          },
          {
            id: 'macos-patch-schedule',
            title: 'Schedule Updates',
            description: 'Schedule macOS updates during off-hours.',
            icon: Activity,
            color: 'text-purple-500',
            risk: 'Low',
            deviceType: 'macos',
            type: 'script',
            lang: 'Shell'
          }
        ],
        linux: [
          {
            id: 'linux-patch-update',
            title: 'Update All Packages',
            description: 'Update all system packages to latest versions using package manager.',
            icon: Download,
            color: 'text-blue-500',
            risk: 'Medium',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          },
          {
            id: 'linux-patch-security',
            title: 'Install Security Updates',
            description: 'Install only security-related package updates.',
            icon: Shield,
            color: 'text-orange-500',
            risk: 'High',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          },
          {
            id: 'linux-patch-auto',
            title: 'Configure Auto-Updates',
            description: 'Configure unattended-upgrades for automatic security updates.',
            icon: Settings,
            color: 'text-green-500',
            risk: 'Medium',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          },
          {
            id: 'linux-patch-kernel',
            title: 'Update Kernel',
            description: 'Update Linux kernel to latest stable version.',
            icon: Server,
            color: 'text-purple-500',
            risk: 'High',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          }
        ],
        ios: [
          {
            id: 'ios-patch-check',
            title: 'Check for Updates',
            description: 'Check for available iOS system updates.',
            icon: Download,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'ios',
            type: 'command'
          },
          {
            id: 'ios-patch-install',
            title: 'Install iOS Update',
            description: 'Install pending iOS system updates.',
            icon: CheckCircle,
            color: 'text-green-500',
            risk: 'Medium',
            deviceType: 'ios',
            type: 'command'
          },
          {
            id: 'ios-patch-auto',
            title: 'Enable Auto-Update',
            description: 'Enable automatic iOS updates installation.',
            icon: Settings,
            color: 'text-orange-500',
            risk: 'Medium',
            deviceType: 'ios',
            type: 'command'
          }
        ]
      },
      'developer-env': {
        windows: [
          {
            id: 'win-dev-vscode',
            title: 'Install Visual Studio Code',
            description: 'Install VS Code with recommended extensions for development.',
            icon: Code,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'windows',
            type: 'script',
            lang: 'PowerShell'
          },
          {
            id: 'win-dev-git',
            title: 'Configure Git',
            description: 'Install and configure Git with user credentials.',
            icon: GitBranch,
            color: 'text-green-500',
            risk: 'Low',
            deviceType: 'windows',
            type: 'script',
            lang: 'PowerShell'
          },
          {
            id: 'win-dev-docker',
            title: 'Install Docker Desktop',
            description: 'Install Docker Desktop for Windows container development.',
            icon: Package,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'windows',
            type: 'script',
            lang: 'PowerShell'
          },
          {
            id: 'win-dev-node',
            title: 'Install Node.js & npm',
            description: 'Install Node.js runtime and npm package manager.',
            icon: Terminal,
            color: 'text-green-500',
            risk: 'Low',
            deviceType: 'windows',
            type: 'script',
            lang: 'PowerShell'
          }
        ],
        android: [
          {
            id: 'android-dev-studio',
            title: 'Install Android Studio',
            description: 'Install Android Studio development environment.',
            icon: Code,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'android',
            type: 'command'
          },
          {
            id: 'android-dev-adb',
            title: 'Enable USB Debugging',
            description: 'Enable USB debugging for Android development.',
            icon: Settings,
            color: 'text-yellow-500',
            risk: 'Medium',
            deviceType: 'android',
            type: 'command'
          },
          {
            id: 'android-dev-sdk',
            title: 'Install SDK Tools',
            description: 'Install Android SDK and development tools.',
            icon: Package,
            color: 'text-green-500',
            risk: 'Low',
            deviceType: 'android',
            type: 'command'
          }
        ],
        macos: [
          {
            id: 'macos-dev-xcode',
            title: 'Install Xcode Command Line Tools',
            description: 'Install Xcode command line tools for macOS development.',
            icon: Code,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'macos',
            type: 'script',
            lang: 'Shell'
          },
          {
            id: 'macos-dev-homebrew',
            title: 'Install Homebrew',
            description: 'Install Homebrew package manager for macOS.',
            icon: Package,
            color: 'text-green-500',
            risk: 'Low',
            deviceType: 'macos',
            type: 'script',
            lang: 'Shell'
          },
          {
            id: 'macos-dev-docker',
            title: 'Install Docker Desktop',
            description: 'Install Docker Desktop for macOS container development.',
            icon: Package,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'macos',
            type: 'script',
            lang: 'Shell'
          },
          {
            id: 'macos-dev-node',
            title: 'Install Node.js via Homebrew',
            description: 'Install Node.js and npm using Homebrew.',
            icon: Terminal,
            color: 'text-green-500',
            risk: 'Low',
            deviceType: 'macos',
            type: 'script',
            lang: 'Shell'
          }
        ],
        linux: [
          {
            id: 'linux-dev-build-tools',
            title: 'Install Build Tools',
            description: 'Install essential build tools (gcc, make, cmake, etc.).',
            icon: Code,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          },
          {
            id: 'linux-dev-docker',
            title: 'Install Docker',
            description: 'Install Docker Engine for container development.',
            icon: Package,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          },
          {
            id: 'linux-dev-git',
            title: 'Configure Git',
            description: 'Install and configure Git with user credentials.',
            icon: GitBranch,
            color: 'text-green-500',
            risk: 'Low',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          },
          {
            id: 'linux-dev-node',
            title: 'Install Node.js',
            description: 'Install Node.js and npm package manager.',
            icon: Terminal,
            color: 'text-green-500',
            risk: 'Low',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          },
          {
            id: 'linux-dev-python',
            title: 'Install Python & pip',
            description: 'Install Python 3 and pip package manager.',
            icon: Code,
            color: 'text-yellow-500',
            risk: 'Low',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          }
        ],
        ios: [
          {
            id: 'ios-dev-xcode',
            title: 'Install Xcode',
            description: 'Install Xcode development environment for iOS.',
            icon: Code,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'ios',
            type: 'command'
          },
          {
            id: 'ios-dev-provisioning',
            title: 'Configure Provisioning Profiles',
            description: 'Set up iOS provisioning profiles for development.',
            icon: Settings,
            color: 'text-yellow-500',
            risk: 'Medium',
            deviceType: 'ios',
            type: 'command'
          }
        ]
      },
      'server-hardening': {
        windows: [
          {
            id: 'win-srv-disable-services',
            title: 'Disable Unnecessary Services',
            description: 'Disable non-essential Windows services to reduce attack surface.',
            icon: Shield,
            color: 'text-red-500',
            risk: 'High',
            deviceType: 'windows',
            type: 'script',
            lang: 'PowerShell'
          },
          {
            id: 'win-srv-firewall-rules',
            title: 'Configure Firewall Rules',
            description: 'Configure strict Windows Firewall rules for server security.',
            icon: Shield,
            color: 'text-orange-500',
            risk: 'High',
            deviceType: 'windows',
            type: 'script',
            lang: 'PowerShell'
          },
          {
            id: 'win-srv-rdp-secure',
            title: 'Secure RDP Access',
            description: 'Harden Remote Desktop Protocol (RDP) security settings.',
            icon: Lock,
            color: 'text-red-500',
            risk: 'High',
            deviceType: 'windows',
            type: 'script',
            lang: 'PowerShell'
          },
          {
            id: 'win-srv-audit-logging',
            title: 'Enable Audit Logging',
            description: 'Enable comprehensive Windows audit logging for security events.',
            icon: FileText,
            color: 'text-blue-500',
            risk: 'Medium',
            deviceType: 'windows',
            type: 'script',
            lang: 'PowerShell'
          }
        ],
        android: [
          {
            id: 'android-srv-disable-bluetooth',
            title: 'Disable Bluetooth',
            description: 'Disable Bluetooth on Android server devices for security.',
            icon: Shield,
            color: 'text-orange-500',
            risk: 'Medium',
            deviceType: 'android',
            type: 'command'
          },
          {
            id: 'android-srv-lockdown',
            title: 'Device Lockdown Mode',
            description: 'Enable strict lockdown mode for Android server devices.',
            icon: Lock,
            color: 'text-red-500',
            risk: 'High',
            deviceType: 'android',
            type: 'command'
          }
        ],
        macos: [
          {
            id: 'macos-srv-firewall',
            title: 'Enable Stealth Mode Firewall',
            description: 'Enable macOS firewall with stealth mode for server security.',
            icon: Shield,
            color: 'text-orange-500',
            risk: 'High',
            deviceType: 'macos',
            type: 'script',
            lang: 'Shell'
          },
          {
            id: 'macos-srv-ssh-secure',
            title: 'Secure SSH Configuration',
            description: 'Harden SSH server configuration on macOS.',
            icon: Lock,
            color: 'text-red-500',
            risk: 'High',
            deviceType: 'macos',
            type: 'script',
            lang: 'Shell'
          },
          {
            id: 'macos-srv-disable-services',
            title: 'Disable Unnecessary Services',
            description: 'Disable non-essential macOS services.',
            icon: Settings,
            color: 'text-orange-500',
            risk: 'Medium',
            deviceType: 'macos',
            type: 'script',
            lang: 'Shell'
          }
        ],
        linux: [
          {
            id: 'linux-srv-ssh-harden',
            title: 'Harden SSH Configuration',
            description: 'Configure secure SSH settings (disable root login, key-only auth).',
            icon: Lock,
            color: 'text-red-500',
            risk: 'High',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          },
          {
            id: 'linux-srv-firewall',
            title: 'Configure iptables/ufw',
            description: 'Set up strict firewall rules for Linux server.',
            icon: Shield,
            color: 'text-orange-500',
            risk: 'High',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          },
          {
            id: 'linux-srv-disable-services',
            title: 'Disable Unnecessary Services',
            description: 'Disable non-essential systemd services.',
            icon: Settings,
            color: 'text-orange-500',
            risk: 'Medium',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          },
          {
            id: 'linux-srv-selinux',
            title: 'Enable SELinux Enforcing',
            description: 'Enable SELinux in enforcing mode for enhanced security.',
            icon: Shield,
            color: 'text-red-500',
            risk: 'High',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          },
          {
            id: 'linux-srv-fail2ban',
            title: 'Install & Configure Fail2ban',
            description: 'Install and configure Fail2ban for intrusion prevention.',
            icon: AlertTriangle,
            color: 'text-orange-500',
            risk: 'High',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          },
          {
            id: 'linux-srv-auditd',
            title: 'Enable Auditd Logging',
            description: 'Configure auditd for comprehensive system audit logging.',
            icon: FileText,
            color: 'text-blue-500',
            risk: 'Medium',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          }
        ],
        ios: [
          {
            id: 'ios-srv-restrictions',
            title: 'Apply Server Restrictions',
            description: 'Apply strict security restrictions for iOS server devices.',
            icon: Shield,
            color: 'text-red-500',
            risk: 'High',
            deviceType: 'ios',
            type: 'command'
          },
          {
            id: 'ios-srv-lockdown',
            title: 'Enable Lockdown Mode',
            description: 'Enable maximum security lockdown mode on iOS.',
            icon: Lock,
            color: 'text-red-500',
            risk: 'High',
            deviceType: 'ios',
            type: 'command'
          }
        ]
      },
      'cis-benchmark': {
        windows: [
          {
            id: 'win-cis-account-policies',
            title: 'CIS Account Policies',
            description: 'Apply CIS Level 1 account management policies.',
            icon: Shield,
            color: 'text-red-500',
            risk: 'High',
            deviceType: 'windows',
            type: 'script',
            lang: 'PowerShell'
          },
          {
            id: 'win-cis-audit-policy',
            title: 'CIS Audit Policy',
            description: 'Configure Windows audit policy per CIS Level 1 standards.',
            icon: FileText,
            color: 'text-orange-500',
            risk: 'High',
            deviceType: 'windows',
            type: 'script',
            lang: 'PowerShell'
          },
          {
            id: 'win-cis-security-options',
            title: 'CIS Security Options',
            description: 'Apply CIS Level 1 security options configuration.',
            icon: Shield,
            color: 'text-red-500',
            risk: 'High',
            deviceType: 'windows',
            type: 'script',
            lang: 'PowerShell'
          },
          {
            id: 'win-cis-user-rights',
            title: 'CIS User Rights Assignment',
            description: 'Configure user rights assignment per CIS Level 1.',
            icon: Key,
            color: 'text-orange-500',
            risk: 'High',
            deviceType: 'windows',
            type: 'script',
            lang: 'PowerShell'
          }
        ],
        android: [
          {
            id: 'android-cis-password',
            title: 'CIS Password Policy',
            description: 'Apply CIS password complexity requirements for Android.',
            icon: Key,
            color: 'text-orange-500',
            risk: 'High',
            deviceType: 'android',
            type: 'command'
          },
          {
            id: 'android-cis-encryption',
            title: 'CIS Encryption Requirements',
            description: 'Enforce device encryption per CIS standards.',
            icon: Shield,
            color: 'text-red-500',
            risk: 'High',
            deviceType: 'android',
            type: 'command'
          },
          {
            id: 'android-cis-screen-lock',
            title: 'CIS Screen Lock Policy',
            description: 'Configure screen lock requirements per CIS Level 1.',
            icon: Lock,
            color: 'text-orange-500',
            risk: 'Medium',
            deviceType: 'android',
            type: 'command'
          }
        ],
        macos: [
          {
            id: 'macos-cis-system-settings',
            title: 'CIS System Settings',
            description: 'Apply CIS Level 1 system configuration settings.',
            icon: Shield,
            color: 'text-red-500',
            risk: 'High',
            deviceType: 'macos',
            type: 'script',
            lang: 'Shell'
          },
          {
            id: 'macos-cis-logging',
            title: 'CIS Logging Configuration',
            description: 'Configure logging per CIS Level 1 requirements.',
            icon: FileText,
            color: 'text-orange-500',
            risk: 'High',
            deviceType: 'macos',
            type: 'script',
            lang: 'Shell'
          },
          {
            id: 'macos-cis-network',
            title: 'CIS Network Security',
            description: 'Apply CIS network security configurations.',
            icon: Shield,
            color: 'text-red-500',
            risk: 'High',
            deviceType: 'macos',
            type: 'script',
            lang: 'Shell'
          }
        ],
        linux: [
          {
            id: 'linux-cis-filesystem',
            title: 'CIS Filesystem Configuration',
            description: 'Configure filesystem settings per CIS Level 1 benchmark.',
            icon: HardDrive,
            color: 'text-orange-500',
            risk: 'High',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          },
          {
            id: 'linux-cis-services',
            title: 'CIS Service Management',
            description: 'Disable unnecessary services per CIS Level 1.',
            icon: Settings,
            color: 'text-orange-500',
            risk: 'High',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          },
          {
            id: 'linux-cis-network',
            title: 'CIS Network Configuration',
            description: 'Apply CIS network security configurations.',
            icon: Shield,
            color: 'text-red-500',
            risk: 'High',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          },
          {
            id: 'linux-cis-logging',
            title: 'CIS Logging & Auditing',
            description: 'Configure logging and auditing per CIS Level 1.',
            icon: FileText,
            color: 'text-orange-500',
            risk: 'High',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          },
          {
            id: 'linux-cis-access-control',
            title: 'CIS Access Control',
            description: 'Configure access control settings per CIS Level 1.',
            icon: Key,
            color: 'text-red-500',
            risk: 'High',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          }
        ],
        ios: [
          {
            id: 'ios-cis-passcode',
            title: 'CIS Passcode Policy',
            description: 'Apply CIS passcode complexity requirements.',
            icon: Key,
            color: 'text-orange-500',
            risk: 'High',
            deviceType: 'ios',
            type: 'command'
          },
          {
            id: 'ios-cis-encryption',
            title: 'CIS Encryption Requirements',
            description: 'Enforce device encryption per CIS standards.',
            icon: Shield,
            color: 'text-red-500',
            risk: 'High',
            deviceType: 'ios',
            type: 'command'
          },
          {
            id: 'ios-cis-restrictions',
            title: 'CIS Restrictions',
            description: 'Apply CIS security restrictions configuration.',
            icon: Shield,
            color: 'text-red-500',
            risk: 'High',
            deviceType: 'ios',
            type: 'command'
          }
        ]
      },
      'system-audit': {
        windows: [
          {
            id: 'win-audit-security',
            title: 'Security Configuration Audit',
            description: 'Audit Windows security configuration and compliance.',
            icon: Shield,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'windows',
            type: 'script',
            lang: 'PowerShell'
          },
          {
            id: 'win-audit-users',
            title: 'User Account Audit',
            description: 'Audit user accounts, permissions, and access rights.',
            icon: UserX,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'windows',
            type: 'script',
            lang: 'PowerShell'
          },
          {
            id: 'win-audit-services',
            title: 'Service Configuration Audit',
            description: 'Audit running services and startup configuration.',
            icon: Settings,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'windows',
            type: 'script',
            lang: 'PowerShell'
          },
          {
            id: 'win-audit-network',
            title: 'Network Configuration Audit',
            description: 'Audit network settings, firewall rules, and connections.',
            icon: Wifi,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'windows',
            type: 'script',
            lang: 'PowerShell'
          },
          {
            id: 'win-audit-software',
            title: 'Installed Software Audit',
            description: 'Audit installed software and applications.',
            icon: Package,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'windows',
            type: 'script',
            lang: 'PowerShell'
          }
        ],
        android: [
          {
            id: 'android-audit-security',
            title: 'Security Compliance Audit',
            description: 'Audit Android device security and compliance status.',
            icon: Shield,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'android',
            type: 'command'
          },
          {
            id: 'android-audit-apps',
            title: 'Installed Apps Audit',
            description: 'Audit installed applications and permissions.',
            icon: Package,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'android',
            type: 'command'
          },
          {
            id: 'android-audit-settings',
            title: 'Device Settings Audit',
            description: 'Audit device settings and configuration.',
            icon: Settings,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'android',
            type: 'command'
          }
        ],
        macos: [
          {
            id: 'macos-audit-security',
            title: 'Security Configuration Audit',
            description: 'Audit macOS security settings and compliance.',
            icon: Shield,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'macos',
            type: 'script',
            lang: 'Shell'
          },
          {
            id: 'macos-audit-users',
            title: 'User Account Audit',
            description: 'Audit user accounts and permissions.',
            icon: UserX,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'macos',
            type: 'script',
            lang: 'Shell'
          },
          {
            id: 'macos-audit-software',
            title: 'Installed Software Audit',
            description: 'Audit installed applications and packages.',
            icon: Package,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'macos',
            type: 'script',
            lang: 'Shell'
          },
          {
            id: 'macos-audit-network',
            title: 'Network Configuration Audit',
            description: 'Audit network settings and firewall configuration.',
            icon: Wifi,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'macos',
            type: 'script',
            lang: 'Shell'
          }
        ],
        linux: [
          {
            id: 'linux-audit-security',
            title: 'Security Configuration Audit',
            description: 'Comprehensive security configuration audit.',
            icon: Shield,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          },
          {
            id: 'linux-audit-users',
            title: 'User Account Audit',
            description: 'Audit user accounts, groups, and sudo access.',
            icon: UserX,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          },
          {
            id: 'linux-audit-services',
            title: 'Service Configuration Audit',
            description: 'Audit running services and systemd configuration.',
            icon: Settings,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          },
          {
            id: 'linux-audit-network',
            title: 'Network Configuration Audit',
            description: 'Audit network settings, firewall, and connections.',
            icon: Wifi,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          },
          {
            id: 'linux-audit-software',
            title: 'Installed Packages Audit',
            description: 'Audit installed packages and software.',
            icon: Package,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          },
          {
            id: 'linux-audit-filesystem',
            title: 'Filesystem Permissions Audit',
            description: 'Audit filesystem permissions and ownership.',
            icon: HardDrive,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'linux',
            type: 'script',
            lang: 'Bash'
          }
        ],
        ios: [
          {
            id: 'ios-audit-security',
            title: 'Security Compliance Audit',
            description: 'Audit iOS device security and compliance status.',
            icon: Shield,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'ios',
            type: 'command'
          },
          {
            id: 'ios-audit-apps',
            title: 'Installed Apps Audit',
            description: 'Audit installed applications and permissions.',
            icon: Package,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'ios',
            type: 'command'
          },
          {
            id: 'ios-audit-settings',
            title: 'Device Settings Audit',
            description: 'Audit device settings and configuration.',
            icon: Settings,
            color: 'text-blue-500',
            risk: 'Low',
            deviceType: 'ios',
            type: 'command'
          }
        ]
      }
    };

    return policies[baselineType]?.[deviceType] || [];
  };

  // Organize policies by device type
  const organizeByDeviceType = () => {
    const deviceTypes = ['windows', 'android', 'macos', 'linux', 'ios'];
    const organized = {};

    deviceTypes.forEach(deviceType => {
      organized[deviceType] = generatePolicies(baselineType, deviceType);
    });

    if (customPolicies) {
      Object.entries(customPolicies).forEach(([deviceType, policies]) => {
        if (!deviceType || !Array.isArray(policies)) return;
        const normalized = policies.map((policy) => ({
          ...policy,
          icon: iconLookup[policy.iconName] || policy.icon || Shield,
          lang: policy.lang || policy.commandType || 'Custom',
          deviceType,
          color: policy.color || 'text-indigo-500',
          type: policy.type || 'script',
          risk: policy.risk || 'Medium',
          category: policy.category || 'custom'
        }));
        organized[deviceType] = [...(organized[deviceType] || []), ...normalized];
      });
    }

    return organized;
  };

  const deviceTypeCards = [
    {
      id: 'windows',
      name: 'Windows',
      icon: Monitor,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      description: 'Windows device policies and configurations'
    },
    {
      id: 'android',
      name: 'Android',
      icon: Smartphone,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      description: 'Android device management policies'
    },
    {
      id: 'macos',
      name: 'macOS',
      icon: Laptop,
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
      description: 'macOS device policies and configurations'
    },
    {
      id: 'linux',
      name: 'Linux',
      icon: Terminal,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      description: 'Linux system policies and configurations'
    },
    {
      id: 'ios',
      name: 'iOS',
      icon: Apple,
      color: 'text-gray-600',
      bgColor: 'bg-gray-50',
      borderColor: 'border-gray-200',
      description: 'iOS device management policies'
    }
  ];

  const organizedPolicies = organizeByDeviceType();
  const selectedPolicies = selectedDeviceType ? organizedPolicies[selectedDeviceType] : [];

  const getRiskBadge = (risk) => {
    const colors = {
      'Low': 'bg-green-100 text-green-800',
      'Medium': 'bg-yellow-100 text-yellow-800',
      'High': 'bg-orange-100 text-orange-800',
      'Critical': 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${colors[risk] || 'bg-gray-100 text-gray-800'}`}>
        {risk}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Device Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {deviceTypeCards.map((deviceType) => {
          const Icon = deviceType.icon;
          const policiesForType = organizedPolicies[deviceType.id] || [];
          const isSelected = selectedDeviceType === deviceType.id;

          return (
            <div
              key={deviceType.id}
              onClick={() => setSelectedDeviceType(isSelected ? null : deviceType.id)}
              className={`${deviceType.bgColor} ${deviceType.borderColor} border-2 rounded-lg p-6 cursor-pointer transition-all hover:shadow-lg ${
                isSelected ? 'ring-2 ring-offset-2 ring-blue-500' : ''
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`${deviceType.color} p-3 rounded-lg bg-white`}>
                  <Icon className="w-8 h-8" />
                </div>
                <span className="px-3 py-1 bg-white rounded-full text-sm font-semibold text-gray-700">
                  {policiesForType.length}
                </span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">{deviceType.name}</h3>
              <p className="text-sm text-gray-600">{deviceType.description}</p>
              {policiesForType.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-2">Available Policies:</p>
                  <div className="space-y-1">
                    {policiesForType.slice(0, 2).map((policy) => (
                      <div key={policy.id} className="text-sm font-medium text-gray-700 truncate">
                        {policy.title}
                      </div>
                    ))}
                    {policiesForType.length > 2 && (
                      <div className="text-xs text-gray-500">
                        +{policiesForType.length - 2} more
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Selected Device Type Policies */}
      {selectedDeviceType && (
        <div className="bg-white rounded-lg shadow border">
          <div className="p-4 border-b flex items-center justify-between">
            <div className="flex items-center">
              {(() => {
                const deviceType = deviceTypeCards.find(d => d.id === selectedDeviceType);
                const Icon = deviceType?.icon || Settings;
                return (
                  <>
                    <Icon className={`w-5 h-5 mr-2 ${deviceType?.color || 'text-gray-600'}`} />
                    <h3 className="text-lg font-semibold">
                      {deviceType?.name} Policies ({selectedPolicies.length})
                    </h3>
                  </>
                );
              })()}
            </div>
            <button
              onClick={() => onCreatePolicy && onCreatePolicy(selectedDeviceType)}
              className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 flex items-center"
            >
              <FileText className="w-4 h-4 mr-1" />
              Create Policy
            </button>
          </div>

          <div className="p-4">
            {selectedPolicies.length === 0 ? (
              <div className="text-center py-12">
                <Shield className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-gray-500 mb-4">No policies for {deviceTypeCards.find(d => d.id === selectedDeviceType)?.name} devices</p>
                <button
                  onClick={() => onCreatePolicy && onCreatePolicy(selectedDeviceType)}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Create First Policy
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {selectedPolicies.map((policy) => {
                  const Icon = policy.icon || Shield;
                  return (
                    <div
                      key={policy.id}
                      className="border rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => onSelectScript && onSelectScript(policy)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className={`${policy.color || 'text-blue-500'} p-2 rounded-lg bg-gray-50`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        {getRiskBadge(policy.risk)}
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-1">{policy.title}</h4>
                      <p className="text-sm text-gray-600 mb-3">{policy.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">
                          {policy.lang || policy.type || 'Policy'}
                        </span>
                        <button className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                          View Details →
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BaselineDeviceCards;

